<!-- components/courses/CourseSettings.vue -->
<template>
  <div class="course-settings">
    <div class="settings-container">
      <div class="settings-header">
        <h2>Course Settings</h2>
        <p>Manage your course details, pricing, and visibility</p>
      </div>

      <form @submit.prevent="saveSettings" class="settings-form">
        <!-- Basic Info -->
        <div class="settings-section">
          <h3>Basic Information</h3>

          <div class="form-group">
            <label>Course Title <span class="required">*</span></label>
            <input
              v-model="form.title"
              type="text"
              placeholder="Enter course title"
              required
            />
            <span class="hint"
              >A clear, descriptive title helps students find your course</span
            >
          </div>

          <div class="form-group">
            <label>Course Description <span class="required">*</span></label>
            <textarea
              v-model="form.description"
              rows="4"
              placeholder="Describe what students will learn..."
              required
            ></textarea>
            <span class="char-count"
              >{{ form.description?.length || 0 }}/5000</span
            >
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Category <span class="required">*</span></label>
              <select v-model="form.category" required>
                <option value="">Select a category...</option>
                <option value="programming">💻 Programming</option>
                <option value="design">🎨 Design</option>
                <option value="business">📊 Business</option>
                <option value="marketing">📈 Marketing</option>
                <option value="ai">🤖 AI & Machine Learning</option>
                <option value="languages">🌍 Languages</option>
                <option value="music">🎵 Music</option>
                <option value="other">📚 Other</option>
              </select>
            </div>

            <div class="form-group">
              <label>Difficulty</label>
              <select v-model="form.difficulty">
                <option value="beginner">🌱 Beginner</option>
                <option value="intermediate">📈 Intermediate</option>
                <option value="advanced">🚀 Advanced</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label>Tags</label>
            <div class="tags-input">
              <input
                v-model="tagInput"
                type="text"
                placeholder="Add tags (press Enter)"
                @keyup.enter="addTag"
              />
              <button type="button" class="add-tag-btn" @click="addTag">
                Add
              </button>
            </div>
            <div class="tags-list">
              <span v-for="tag in form.tags" :key="tag" class="tag">
                #{{ tag }}
                <button
                  type="button"
                  @click="removeTag(tag)"
                  class="remove-tag"
                >
                  ×
                </button>
              </span>
            </div>
          </div>
        </div>

        <!-- Pricing -->
        <div class="settings-section">
          <h3>Pricing</h3>

          <div class="form-row">
            <div class="form-group">
              <label>Price (USD)</label>
              <div class="price-input">
                <span class="currency">$</span>
                <input
                  v-model.number="form.price"
                  type="number"
                  min="0"
                  step="0.99"
                  placeholder="0.00"
                />
              </div>
              <span class="hint">Set to 0 for free courses</span>
            </div>

            <div class="form-group">
              <label>Currency</label>
              <select v-model="form.currency">
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="GBP">GBP - British Pound</option>
                <option value="IQD">IQD - Iraqi Dinar</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Thumbnail -->
        <div class="settings-section">
          <h3>Course Thumbnail</h3>

          <div class="thumbnail-upload">
            <div v-if="form.thumbnail" class="thumbnail-preview">
              <img :src="form.thumbnail" alt="Course thumbnail" />
              <button
                type="button"
                class="remove-thumbnail"
                @click="removeThumbnail"
              >
                ×
              </button>
              <div class="thumbnail-overlay">
                <button
                  type="button"
                  class="change-thumbnail"
                  @click="triggerThumbnailUpload"
                >
                  Change Image
                </button>
              </div>
            </div>
            <div
              v-else
              class="thumbnail-placeholder"
              @click="triggerThumbnailUpload"
            >
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <p>Click to upload a thumbnail</p>
              <span class="hint">Recommended: 1280x720px (16:9 ratio)</span>
              <span class="hint">Max: 5MB • JPG, PNG, WebP</span>
            </div>
            <input
              ref="thumbnailInput"
              type="file"
              accept="image/*"
              @change="handleThumbnailUpload"
              style="display: none"
            />
          </div>
        </div>

        <!-- Social Links -->
        <div class="settings-section">
          <h3>Social Links</h3>
          <p class="section-hint">
            Connect your course with your social media profiles
          </p>

          <div class="social-links">
            <div
              v-for="(url, platform) in form.socialLinks"
              :key="platform"
              class="social-input-row"
            >
              <span class="social-icon">{{ getSocialIcon(platform) }}</span>
              <input
                v-model="form.socialLinks[platform]"
                type="url"
                :placeholder="`Enter ${platform} URL`"
              />
              <button
                type="button"
                class="remove-social"
                @click="removeSocial(platform)"
              >
                ×
              </button>
            </div>
          </div>

          <div class="add-social">
            <select v-model="newSocialPlatform">
              <option value="">Add social link...</option>
              <option value="github">🐙 GitHub</option>
              <option value="website">🌐 Website</option>
              <option value="linkedin">💼 LinkedIn</option>
              <option value="twitter">🐦 Twitter/X</option>
              <option value="youtube">▶️ YouTube</option>
              <option value="instagram">📸 Instagram</option>
              <option value="facebook">📘 Facebook</option>
              <option value="tiktok">🎵 TikTok</option>
            </select>
            <button
              type="button"
              class="add-btn"
              @click="addSocial"
              :disabled="!newSocialPlatform"
            >
              Add Link
            </button>
          </div>
        </div>

        <!-- Visibility -->
        <div class="settings-section">
          <h3>Visibility & Publishing</h3>

          <div class="visibility-options">
            <button
              type="button"
              class="visibility-btn"
              :class="{ active: form.isPublished }"
              @click="form.isPublished = !form.isPublished"
            >
              <span class="icon">{{ form.isPublished ? "🌐" : "📝" }}</span>
              <span class="label">{{
                form.isPublished ? "Published" : "Draft"
              }}</span>
              <span class="description">
                {{
                  form.isPublished
                    ? "Course is visible to students"
                    : "Course is hidden from students"
                }}
              </span>
            </button>
          </div>

          <div v-if="form.isPublished" class="publish-info">
            <span class="icon">✅</span>
            <span>This course will be published immediately</span>
          </div>
          <div v-else class="draft-info">
            <span class="icon">📝</span>
            <span
              >This course is in draft mode. Complete all sections before
              publishing.</span
            >
          </div>
        </div>

        <!-- Form Actions -->
        <div class="form-actions">
          <button type="button" class="btn-secondary" @click="$emit('update')">
            Cancel
          </button>
          <button type="submit" class="btn-primary" :disabled="saving">
            <span v-if="saving" class="spinner-small"></span>
            <span v-else>Save Settings</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
<!-- components/courses/CourseSettings.vue -->
<script setup>
import { ref, reactive, onMounted, watch } from "vue";
import { useCourseStore } from "@/stores/course";
import { uploadThumbnailFile, deleteFile } from "@/services/cloudinary";

const props = defineProps({
  course: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["update"]);

const courseStore = useCourseStore();
const saving = ref(false);
const thumbnailInput = ref(null);
const newSocialPlatform = ref("");
const tagInput = ref("");
const uploadingThumbnail = ref(false);

const form = reactive({
  title: "",
  description: "",
  category: "",
  difficulty: "beginner",
  price: 0,
  currency: "USD",
  thumbnail: "",
  thumbnail_public_id: "",
  socialLinks: {},
  tags: [],
  isPublished: false,
});

// Initialize form with course data
onMounted(() => {
  Object.assign(form, {
    title: props.course.title || "",
    description: props.course.description || "",
    category: props.course.category || "",
    difficulty: props.course.difficulty || "beginner",
    price: props.course.price || 0,
    currency: props.course.currency || "USD",
    thumbnail: props.course.thumbnail || "",
    thumbnail_public_id: props.course.thumbnail_public_id || "",
    socialLinks: props.course.socialLinks || {},
    tags: props.course.tags || [],
    isPublished: props.course.isPublished || false,
  });
});

// Watch for course prop changes
watch(
  () => props.course,
  (newCourse) => {
    if (newCourse) {
      form.thumbnail = newCourse.thumbnail || "";
      form.thumbnail_public_id = newCourse.thumbnail_public_id || "";
    }
  },
  { deep: true },
);

const addTag = () => {
  const tag = tagInput.value.trim().toLowerCase().replace(/\s+/g, "-");
  if (tag && !form.tags.includes(tag)) {
    form.tags.push(tag);
    tagInput.value = "";
  }
};

const removeTag = (tag) => {
  form.tags = form.tags.filter((t) => t !== tag);
};

const getSocialIcon = (platform) => {
  const icons = {
    github: "🐙",
    website: "🌐",
    linkedin: "💼",
    twitter: "🐦",
    youtube: "▶️",
    instagram: "📸",
    facebook: "📘",
    tiktok: "🎵",
  };
  return icons[platform] || "🔗";
};

const addSocial = () => {
  if (newSocialPlatform.value && !form.socialLinks[newSocialPlatform.value]) {
    form.socialLinks[newSocialPlatform.value] = "";
    newSocialPlatform.value = "";
  }
};

const removeSocial = (platform) => {
  delete form.socialLinks[platform];
  form.socialLinks = { ...form.socialLinks };
};

const triggerThumbnailUpload = () => {
  thumbnailInput.value?.click();
};

const handleThumbnailUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  if (file.size > 5 * 1024 * 1024) {
    alert("File too large! Maximum 5MB.");
    return;
  }

  if (!file.type.startsWith("image/")) {
    alert("Please select a valid image file.");
    return;
  }

  uploadingThumbnail.value = true;

  try {
    console.log(`📤 Uploading thumbnail: ${file.name}`);

    const result = await uploadThumbnailFile(file, (progress) => {
      console.log(`Thumbnail upload: ${Math.round(progress)}%`);
    });

    if (result.success) {
      // Store the old public_id for cleanup
      const oldPublicId = form.thumbnail_public_id;

      // Update form with new thumbnail
      form.thumbnail = result.data.url;
      form.thumbnail_public_id = result.data.public_id;

      // Delete old thumbnail if it exists and is different
      if (oldPublicId && oldPublicId !== result.data.public_id) {
        await deleteFile(oldPublicId, "image");
      }

      // Auto-save the thumbnail to the course
      await saveThumbnailToCourse(result.data.url, result.data.public_id);

      alert("✅ Thumbnail uploaded successfully!");
    } else {
      alert("Thumbnail upload failed: " + result.error);
    }
  } catch (error) {
    console.error("Thumbnail upload failed:", error);
    alert("Failed to upload thumbnail: " + error.message);
  } finally {
    uploadingThumbnail.value = false;
    event.target.value = ""; // Reset file input
  }
};

// Save thumbnail to course without saving all settings
const saveThumbnailToCourse = async (thumbnailUrl, publicId) => {
  try {
    const result = await courseStore.updateCourse(props.course.id, {
      thumbnail: thumbnailUrl,
      thumbnail_public_id: publicId,
    });

    if (result.success) {
      console.log("✅ Thumbnail saved to course");
      emit("update");
    } else {
      console.error("Failed to save thumbnail to course:", result.error);
    }
  } catch (error) {
    console.error("Error saving thumbnail:", error);
  }
};

const removeThumbnail = async () => {
  if (!confirm("Remove this thumbnail?")) return;

  const oldPublicId = form.thumbnail_public_id;

  // Delete from Cloudinary
  if (oldPublicId) {
    await deleteFile(oldPublicId, "image");
  }

  form.thumbnail = "";
  form.thumbnail_public_id = "";

  // Auto-save the removal
  await saveThumbnailToCourse("", "");
};

const saveSettings = async () => {
  saving.value = true;

  const result = await courseStore.updateCourse(props.course.id, {
    title: form.title,
    description: form.description,
    category: form.category,
    difficulty: form.difficulty,
    price: form.price,
    currency: form.currency,
    thumbnail: form.thumbnail,
    thumbnail_public_id: form.thumbnail_public_id,
    socialLinks: form.socialLinks,
    tags: form.tags,
    isPublished: form.isPublished,
  });

  saving.value = false;

  if (result.success) {
    emit("update");
    alert("✅ Course settings saved successfully!");
  } else {
    alert("❌ Failed to save settings: " + result.error);
  }
};
</script>

<style lang="scss" scoped>
$primary-orange: #ff8000;

.course-settings {
  .settings-container {
    max-width: 800px;
    margin: 0 auto;
  }

  .settings-header {
    margin-bottom: 32px;

    h2 {
      margin: 0 0 4px 0;
      color: #1a1a1a;
      font-size: 24px;
    }

    p {
      margin: 0;
      color: #868e96;
    }
  }

  .settings-section {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;
    border: 1px solid #e9ecef;

    h3 {
      margin: 0 0 4px 0;
      color: #1a1a1a;
      font-size: 18px;
    }

    .section-hint {
      margin: 0 0 16px 0;
      color: #868e96;
      font-size: 14px;
    }
  }

  .form-group {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }

    label {
      display: block;
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 6px;
      font-size: 14px;

      .required {
        color: #dc3545;
      }
    }

    input,
    textarea,
    select {
      width: 100%;
      padding: 10px 12px;
      border: 2px solid #e9ecef;
      border-radius: 8px;
      font-size: 14px;
      transition: all 0.3s ease;

      &:focus {
        border-color: $primary-orange;
        outline: none;
        box-shadow: 0 0 0 4px rgba(255, 128, 0, 0.1);
      }
    }

    textarea {
      resize: vertical;
      font-family: inherit;
      min-height: 80px;
    }

    .hint {
      display: block;
      font-size: 12px;
      color: #868e96;
      margin-top: 4px;
    }

    .char-count {
      display: block;
      text-align: right;
      font-size: 12px;
      color: #868e96;
      margin-top: 4px;
    }
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .price-input {
    position: relative;
    display: flex;
    align-items: center;

    .currency {
      position: absolute;
      left: 12px;
      font-weight: 600;
      color: #495057;
    }

    input {
      padding-left: 28px;
    }
  }

  .tags-input {
    display: flex;
    gap: 8px;

    input {
      flex: 1;
    }

    .add-tag-btn {
      padding: 10px 16px;
      background: $primary-orange;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      cursor: pointer;
      white-space: nowrap;

      &:hover {
        background: darken($primary-orange, 10%);
      }
    }
  }

  .tags-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;

    .tag {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 12px;
      background: rgba(255, 128, 0, 0.1);
      color: $primary-orange;
      border-radius: 12px;
      font-size: 13px;
      font-weight: 500;

      .remove-tag {
        background: none;
        border: none;
        color: $primary-orange;
        cursor: pointer;
        font-size: 16px;
        padding: 0 4px;

        &:hover {
          color: darken($primary-orange, 20%);
        }
      }
    }
  }

  .thumbnail-upload {
    border: 2px dashed #dee2e6;
    border-radius: 12px;
    padding: 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    min-height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    &:hover {
      border-color: $primary-orange;
      background: rgba(255, 128, 0, 0.05);
    }

    .thumbnail-preview {
      position: relative;
      max-width: 400px;
      width: 100%;

      img {
        width: 100%;
        border-radius: 8px;
        display: block;
      }

      .remove-thumbnail {
        position: absolute;
        top: -8px;
        right: -8px;
        width: 28px;
        height: 28px;
        background: #dc3545;
        color: #fff;
        border: none;
        border-radius: 50%;
        font-size: 18px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1;

        &:hover {
          background: darken(#dc3545, 10%);
        }
      }

      .thumbnail-overlay {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 12px;
        background: rgba(0, 0, 0, 0.6);
        border-radius: 0 0 8px 8px;
        display: flex;
        justify-content: center;

        .change-thumbnail {
          padding: 4px 12px;
          background: rgba(255, 255, 255, 0.2);
          color: #fff;
          border: none;
          border-radius: 4px;
          cursor: pointer;

          &:hover {
            background: rgba(255, 255, 255, 0.3);
          }
        }
      }
    }

    .thumbnail-placeholder {
      text-align: center;
      color: #868e96;

      svg {
        color: #adb5bd;
        margin-bottom: 8px;
      }

      p {
        margin: 4px 0;
        font-weight: 500;
        color: #1a1a1a;
      }

      .hint {
        display: block;
        font-size: 12px;
        color: #868e96;
      }
    }
  }

  .social-links {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 8px;

    .social-input-row {
      display: flex;
      align-items: center;
      gap: 8px;

      .social-icon {
        font-size: 20px;
        min-width: 32px;
        text-align: center;
      }

      input {
        flex: 1;
        padding: 8px 12px;
        border: 2px solid #e9ecef;
        border-radius: 8px;
        font-size: 14px;

        &:focus {
          border-color: $primary-orange;
          outline: none;
        }
      }

      .remove-social {
        background: none;
        border: none;
        font-size: 20px;
        color: #dc3545;
        cursor: pointer;
        padding: 0 4px;

        &:hover {
          color: darken(#dc3545, 10%);
        }
      }
    }
  }

  .add-social {
    display: flex;
    gap: 8px;

    select {
      flex: 1;
      padding: 8px 12px;
      border: 2px solid #e9ecef;
      border-radius: 8px;
      font-size: 14px;

      &:focus {
        border-color: $primary-orange;
        outline: none;
      }
    }

    .add-btn {
      padding: 8px 16px;
      background: $primary-orange;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      cursor: pointer;
      white-space: nowrap;

      &:hover:not(:disabled) {
        background: darken($primary-orange, 10%);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }

  .visibility-options {
    display: flex;
    gap: 12px;

    .visibility-btn {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      padding: 16px;
      background: #fff;
      border: 2px solid #e9ecef;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        border-color: $primary-orange;
      }

      &.active {
        border-color: $primary-orange;
        background: rgba(255, 128, 0, 0.05);
      }

      .icon {
        font-size: 28px;
      }

      .label {
        font-weight: 600;
        color: #1a1a1a;
      }

      .description {
        font-size: 12px;
        color: #868e96;
        text-align: center;
      }
    }
  }

  .publish-info,
  .draft-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-radius: 8px;
    margin-top: 12px;
    font-size: 14px;

    .icon {
      font-size: 20px;
    }
  }

  .publish-info {
    background: rgba(40, 167, 69, 0.1);
    color: #28a745;
  }

  .draft-info {
    background: rgba(255, 193, 7, 0.1);
    color: #856404;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #e9ecef;

    .btn-secondary {
      padding: 10px 24px;
      background: #e9ecef;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      color: #495057;
      cursor: pointer;

      &:hover {
        background: #dee2e6;
      }
    }

    .btn-primary {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 32px;
      background: $primary-orange;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover:not(:disabled) {
        background: darken($primary-orange, 10%);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .spinner-small {
        width: 20px;
        height: 20px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top: 2px solid #fff;
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
      }
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
